import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams, HttpResponse} from '@angular/common/http';
import {catchError, from, map, Observable, switchMap, throwError} from 'rxjs';
import {ServerEndpoints} from '../../../../server.endpoints';
import {FiscalJobDetail} from '../../_models/fiscal-jobs/fiscal-job.models';
import {
  archiveExtension,
  archiveFormatFromMediaType,
  DEFAULT_ARCHIVE_FORMAT,
  FiscalArchiveFormat,
  FiscalExportCreateRequest,
  FiscalExportFile,
  FiscalExportRegister
} from '../../_models/exports/data-export.models';

/**
 * The typed HTTP surface of the DSFinV-K data export (#770) — three calls, no state.
 *
 * ### Why listing past exports is NOT here
 *
 * A past export is a fiscal job, and the engine's own `/fiscal/jobs/List?kind=dataExport` is the queryable
 * record of one (#768). Adding a second list here would be a second, weaker copy of state the engine already
 * holds — which is exactly what makes a list and a detail view come to disagree. This service holds only what
 * the engine cannot answer: the filter's options, the validated create, and the file itself.
 *
 * ### Why a download is a request and not an `<a href>`
 *
 * The session's bearer token travels in an `Authorization` header that an interceptor adds; a plain link
 * carries none, so the browser would fetch an anonymous request and get a 401. The file is therefore read as a
 * blob and saved from memory.
 *
 * ### The file name is the SERVER'S
 *
 * `FiscalExportNaming` decides it, because a file name is the one piece of this feature that leaves the
 * application — it is saved to a disk, attached to an e-mail and handed to a tax office, and it keeps whatever
 * it says. {@link readFileName} reads it out of `Content-Disposition`, which the gateway exposes for exactly
 * this. The fallback is deliberately GENERIC rather than a second implementation of the naming rule: a
 * re-implementation would drift from the server's silently, and a name nobody can mistake for the contract is
 * the honest way to fail. Its EXTENSION, though, is read from the response's own `Content-Type` (#823) —
 * safelisted cross-origin, and set by the server from its read of the bytes — so a fallback name still says
 * what the file is.
 *
 * ### A failed download's body is decoded before it is re-thrown
 *
 * `responseType: 'blob'` applies to the error body as well, so a refusal would otherwise arrive as a `Blob`
 * and `error.error.code` would be `undefined` — every catalogued refusal of a download collapsing into the
 * generic fallback sentence. See {@link withReadableError}.
 */
@Injectable({providedIn: 'root'})
export class DataExportHttpService {
  /**
   * What a download is called when neither the server's name nor its media type could be read.
   *
   * **`.tar`, because that is what the file IS** when nothing says otherwise — the container
   * `FiscalArchive.Default` names. #770 shipped every export under a `.zip` name while the bytes were a
   * POSIX tar, so an auditor's unpacker refused a file this application had just named for them; a fallback
   * that guesses the extension would keep that alive in exactly the situation where nobody can check it.
   * {@link fallbackFileName} takes the extension from the response's own `Content-Type` instead (#823).
   */
  static readonly FALLBACK_FILE_NAME = 'DSFinV-K-Export.tar';

  // `BASE_URL` is a string DI token provided by the app bootstrap, so it is taken through @Inject rather than
  // inject() — the same shape every other http service in this module uses.
  constructor(@Inject('BASE_URL') private readonly baseUrl: string, private readonly http: HttpClient) {
  }

  /**
   * The registers of the selected location, for the create form's filter.
   *
   * Includes decommissioned ones: they produced the records an auditor is asking for, and an export that
   * silently omitted them would be incomplete in a way nobody could see from the file.
   *
   * **A missing `name` is normalised to `null` here, and that matters** (#770, live screen). The monolith serialises
   * with `NullValueHandling.Ignore`, so a register nobody has named arrives with the property ABSENT rather
   * than `null` — and `undefined !== null`, so every `=== null` test downstream would quietly answer "this
   * one has a name" and then render nothing where the name should be. Normalising once, at the boundary the
   * wire quirk belongs to, keeps the rest of this feature able to ask the plain question.
   */
  registers(): Observable<FiscalExportRegister[]> {
    return this.http
      .get<Partial<FiscalExportRegister>[]>(`${this.baseUrl}${ServerEndpoints.Fiscal_Exports_Registers}`)
      .pipe(map(rows => rows.map(row => ({
        id: row.id ?? '',
        name: row.name ?? null,
        registered: row.registered === true
      }))));
  }

  /**
   * Starts a data-export run.
   *
   * Answers as soon as the run is durable and scheduled — it does not wait for the file. The caller then
   * watches it through the fiscal job store.
   */
  create(request: FiscalExportCreateRequest): Observable<FiscalJobDetail> {
    return this.http.post<FiscalJobDetail>(
      `${this.baseUrl}${ServerEndpoints.Fiscal_Exports_Create}`, request);
  }

  /**
   * Collects a finished export's file.
   *
   * **Never rebuilds.** The server reads the reference the run's entry already holds and fetches that one
   * artefact, so asking again returns the same archive.
   */
  download(jobId: string, itemId: string): Observable<FiscalExportFile> {
    const params = new HttpParams().set('jobId', jobId).set('itemId', itemId);

    return this.http
      .get(`${this.baseUrl}${ServerEndpoints.Fiscal_Exports_Download}`,
        {params, observe: 'response', responseType: 'blob'})
      .pipe(
        map(response => ({
          // The fallback's extension comes from the response's own `Content-Type`, which the server sets from
          // its read of the BYTES — never from the run's stored `format`, which records only what was asked
          // for. `Content-Type` is CORS-safelisted, so it is readable in exactly the case the file name is
          // not (#823). With ZIP the default container, a hard-coded `.tar` here would be #770's defect with
          // the roles swapped: zip bytes saved under a name no unpacker will open.
          fileName: DataExportHttpService.readFileName(response, DataExportHttpService.fallbackFileName(
            archiveFormatFromMediaType(response.headers.get('content-type')))),
          blob: response.body ?? new Blob()
        })),
        catchError((error: HttpErrorResponse) => DataExportHttpService.withReadableError(error)));
  }

  /**
   * Re-throws a failed download with its `ApiError` body readable.
   *
   * **Why this is needed at all.** `responseType: 'blob'` applies to the ERROR body too, so a refusal arrives
   * as `error.error instanceof Blob` and `err.error.code` — the value every caller branches on and the whole
   * point of the catalogued codes — is `undefined`. Every refusal of a download therefore reached the operator
   * as the generic fallback sentence, whatever the server had actually said (#770 review D4: the gone case
   * has its own code and its own instruction, and neither could have been shown).
   *
   * The blob is decoded asynchronously and the error re-thrown with a parsed body, so callers keep branching
   * on `error.error.code` exactly as they do for a JSON endpoint. A body that is not JSON is passed through
   * untouched rather than swallowed — an empty or HTML error page is still an error.
   */
  private static withReadableError(error: HttpErrorResponse): Observable<never> {
    if (!(error.error instanceof Blob)) return throwError(() => error);

    return from(error.error.text()).pipe(
      switchMap(text => throwError(() => new HttpErrorResponse({
        error: DataExportHttpService.parseJson(text),
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined
      })))
    );
  }

  /** The body as an object when it is JSON, and the raw text when it is not. */
  private static parseJson(text: string): unknown {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  /**
   * The generic name a download falls back to, with the extension of the container it actually is.
   *
   * Still deliberately NOT a re-implementation of the server's naming rule — it carries no period and no
   * register, so nobody can mistake it for the contract. It only avoids the one thing that is actively
   * harmful: telling the reader the file is something it is not.
   *
   * @param format the container, read from the response's `Content-Type` by the caller. Defaults to
   * {@link DEFAULT_ARCHIVE_FORMAT} for a response that states none.
   */
  static fallbackFileName(format: FiscalArchiveFormat = DEFAULT_ARCHIVE_FORMAT): string {
    return `DSFinV-K-Export${archiveExtension(format)}`;
  }

  /**
   * The name the server chose for the file, out of `Content-Disposition`.
   *
   * Handles both spellings a server may send (`filename=` and the RFC 5987 `filename*=UTF-8''…`), because an
   * export of a register with an umlaut in its name is an ordinary thing and the two forms carry it
   * differently. Falls back to `fallback` (by default {@link FALLBACK_FILE_NAME}) when the header is absent —
   * which, cross-origin, means the gateway is not exposing it. A readable name always wins: it is the
   * server's own decision and carries the period and the register, which no fallback does.
   */
  static readFileName(response: HttpResponse<unknown>,
                      fallback: string = DataExportHttpService.FALLBACK_FILE_NAME): string {
    const header = response.headers.get('content-disposition');
    if (!header) return fallback;

    const encoded = /filename\*=\s*UTF-8''([^;\r\n]+)/i.exec(header);
    if (encoded) {
      try {
        return decodeURIComponent(encoded[1].trim());
      } catch {
        return fallback;
      }
    }

    const plain = /filename=\s*"?([^;"\r\n]+)"?/i.exec(header);
    return plain ? plain[1].trim() : fallback;
  }
}
